import { useEffect, useId, useLayoutEffect, useRef, useState, type ClipboardEvent } from 'react';
import {
  QUESTION_REQUIREMENTS_MAX_LENGTH,
  QUESTION_REQUIREMENTS_MAX_RUNS,
  type QuestionRequirementsRichText,
} from '@wx-weiqi/contracts';
import { noteRangeIsBold, type ClassroomNoteFormat } from '../domain/classroom-note-format';
import {
  readRequirementsEditor,
  formatRequirementsRange,
  writeRequirementsEditor,
} from '../domain/question-requirements-editor';
import './question-requirements.css';

interface Props {
  readonly initialText: string;
  readonly initialRichText?: QuestionRequirementsRichText | null;
  readonly disabled: boolean;
  readonly onChange: (
    text: string,
    richText: QuestionRequirementsRichText | null,
    valid: boolean,
  ) => void;
}

export function QuestionRequirementsEditor({
  initialText,
  initialRichText,
  disabled,
  onChange,
}: Props) {
  const editorRef = useRef<HTMLDivElement>(null);
  const savedRange = useRef<Range | null>(null);
  const initial = useRef({ initialText, initialRichText });
  const composing = useRef(false);
  const [bold, setBold] = useState(false);
  const [colorOpen, setColorOpen] = useState(false);
  const [length, setLength] = useState(initialText.length);
  const [error, setError] = useState<string | null>(null);
  const helpId = useId();
  const colorId = useId();

  useLayoutEffect(() => {
    const editor = editorRef.current;
    if (!editor) return;
    writeRequirementsEditor(editor, initial.current.initialText, initial.current.initialRichText);
    editor.focus({ preventScroll: true });
  }, []);

  const rememberSelection = () => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection?.rangeCount) return;
    const range = selection.getRangeAt(0);
    if (!editor.contains(range.commonAncestorContainer)) return;
    savedRange.current = range.cloneRange();
    setBold(!range.collapsed && noteRangeIsBold(editor, range));
  };

  useEffect(() => {
    document.addEventListener('selectionchange', rememberSelection);
    return () => document.removeEventListener('selectionchange', rememberSelection);
  }, []);

  const commit = () => {
    const editor = editorRef.current;
    if (!editor || composing.current) return;
    const value = readRequirementsEditor(editor);
    const tooLong = value.requirements.length > QUESTION_REQUIREMENTS_MAX_LENGTH;
    const tooFragmented =
      (value.requirementsRichText?.length ?? 0) > QUESTION_REQUIREMENTS_MAX_RUNS;
    const valid = !tooLong && !tooFragmented;
    setLength(value.requirements.length);
    setError(
      tooLong
        ? '题目要求最多 4000 字，请缩短内容后保存。'
        : tooFragmented
          ? '文字格式片段过多，请合并部分格式后保存。'
          : null,
    );
    onChange(value.requirements, value.requirementsRichText, valid);
    rememberSelection();
  };

  const applyFormat = (format: ClassroomNoteFormat | 'bold') => {
    const editor = editorRef.current;
    const selection = document.getSelection();
    const range = savedRange.current;
    if (!editor || !selection || disabled || composing.current) return;
    if (!range || range.collapsed || !editor.contains(range.commonAncestorContainer)) {
      setError('请先选中要设置格式的文字。');
      return;
    }
    const result = formatRequirementsRange(
      editor,
      range.cloneRange(),
      format === 'bold' ? { fontWeight: noteRangeIsBold(editor, range) ? '400' : '700' } : format,
    );
    editor.focus({ preventScroll: true });
    selection.removeAllRanges();
    selection.addRange(result);
    savedRange.current = result.cloneRange();
    commit();
  };

  const paste = (event: ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    const editor = editorRef.current;
    const selection = document.getSelection();
    if (!editor || !selection || disabled) return;
    const range = selection.rangeCount ? selection.getRangeAt(0) : document.createRange();
    if (!editor.contains(range.commonAncestorContainer)) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    const text = event.clipboardData.getData('text/plain').replace(/\r\n?/gu, '\n');
    if (
      readRequirementsEditor(editor).requirements.length - range.toString().length + text.length >
      QUESTION_REQUIREMENTS_MAX_LENGTH
    ) {
      setError('题目要求最多 4000 字，请缩短粘贴内容。');
      return;
    }
    range.deleteContents();
    const node = document.createTextNode(text);
    range.insertNode(node);
    range.setStartAfter(node);
    range.collapse(true);
    selection.removeAllRanges();
    selection.addRange(range);
    commit();
  };

  return (
    <div className="question-requirements-field">
      <div
        className="question-requirements-toolbar"
        role="toolbar"
        aria-label="题目要求文字格式"
        onPointerDownCapture={rememberSelection}
      >
        <button
          type="button"
          aria-label="加粗"
          title="加粗 / 取消加粗"
          aria-pressed={bold}
          disabled={disabled}
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => applyFormat('bold')}
        >
          <b>B</b>
        </button>
        <div className="question-requirements-color">
          <button
            type="button"
            aria-label="字体颜色"
            title="字体颜色"
            aria-expanded={colorOpen}
            aria-controls={colorId}
            disabled={disabled}
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => setColorOpen((open) => !open)}
          >
            <span className="question-requirements-color-icon">A</span>
          </button>
          {colorOpen && (
            <div
              id={colorId}
              className="question-requirements-palette"
              role="group"
              aria-label="选择字体颜色"
            >
              {(
                [
                  ['红色', '#b53a32'],
                  ['黑色', '#000000'],
                ] as const
              ).map(([label, color]) => (
                <button
                  key={label}
                  type="button"
                  disabled={disabled}
                  onPointerDown={(event) => event.preventDefault()}
                  onClick={() => {
                    applyFormat({ color });
                    setColorOpen(false);
                  }}
                >
                  <span aria-hidden="true" style={{ backgroundColor: color }} />
                  {label}
                </button>
              ))}
            </div>
          )}
        </div>
        <span>选中文字后设置</span>
      </div>
      <div
        ref={editorRef}
        className="question-requirements-input"
        role="textbox"
        aria-label="题目要求"
        aria-multiline="true"
        aria-describedby={helpId}
        aria-disabled={disabled}
        contentEditable={!disabled}
        suppressContentEditableWarning
        spellCheck={false}
        data-placeholder="请输入本题要求；留空时学生端显示黑先或白先。"
        onInput={commit}
        onPaste={paste}
        onDrop={(event) => event.preventDefault()}
        onCompositionStart={() => {
          composing.current = true;
        }}
        onCompositionEnd={() => {
          composing.current = false;
          commit();
        }}
        onKeyUp={rememberSelection}
        onPointerUp={rememberSelection}
        onBlur={rememberSelection}
        onKeyDown={(event) => {
          if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'b') {
            event.preventDefault();
            rememberSelection();
            applyFormat('bold');
          }
          if (event.key === 'Escape') setColorOpen(false);
        }}
      />
      <small id={helpId}>{length} / 4000 字 · 保存后对已布置的作业也生效。</small>
      {error && (
        <p className="question-requirements-error" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
